import { Box, Text, HStack, Card, VStack, Spinner } from '@chakra-ui/react';
import { PieChart, Pie, Cell, Tooltip, TooltipProps } from 'recharts';
import { useState } from 'react';
import { useGetTotalSell } from '@/services/dashboard';
import CustomSelect from '@/components/common/CustomSelect';

// Currency symbol function
export const getCurrencySymbol = (currency?: string): string => {
  return currency === 'NPR' ? 'Rs.' : '$';
};

// Colors
const COLORS = ['#FFA500', '#800000']; // Yellow & Maroon

// Custom Tooltip Component
const CustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    return (
      <Box bg="black" p={3} borderRadius="md" color="white">
        {payload.map((entry, index) => (
          <HStack key={`item-${index}`}>
            <Box bg={entry.payload.fill} h={4} w={4} rounded={'sm'} />
            <Text color={'#fffb'}>{`${entry.name}: ${getCurrencySymbol(
              entry.payload.currency,
            )}${entry.value}`}</Text>
          </HStack>
        ))}
      </Box>
    );
  }

  return null;
};

const timeframeOptions = [
  { id: 'weekly', label: 'This Week' },
  { id: 'daily', label: 'Today' },
  { id: 'monthly', label: 'This Month' },
];

const TotalSell = () => {
  const [timeframe, setTimeframe] = useState('weekly');
  const { data: totalSellData, isLoading, isError } = useGetTotalSell(timeframe);

  // Extracting data for Recharts
  const chartData =
    totalSellData?.category_breakdown.map((category) => ({
      name: category.category,
      value: category.total_sales,
      currency: totalSellData.currency, // Include currency in chartData
    })) || [];

  return (
    <Card
      h={450}
      fontFamily={'primary'}
      borderColor={'border.default60'}
      p={4}
      shadow={0}
      borderWidth={'1px'}
      position={'relative'}
    >
      <HStack justify={'space-between'} align={'center'} gap={10}>
        <Text fontSize="xl" fontWeight="semibold">
          Total Sell
        </Text>
        <Box width="fit-content">
          <CustomSelect
            key={timeframe} // Force re-render on timeframe change
            options={timeframeOptions}
            selectedValue={timeframe}
            onChange={setTimeframe}
            placeholder="Select timeframe"
          />
        </Box>
      </HStack>

      {isLoading ? (
        <VStack
          height="380px" // Adjusted for header height
          width="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner />
        </VStack>
      ) : isError ||
        !totalSellData ||
        !totalSellData.category_breakdown ||
        totalSellData.category_breakdown.length === 0 ||
        (totalSellData.total_quantity === 0 && totalSellData.total_sales === 0) ? (
        <VStack
          height="380px" // Adjusted for header height
          width="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <VStack spacing={1}>
            <Text color="text.primary" fontSize="xl" fontWeight="semibold">
              No data available
            </Text>
            <Text color="text.disabled" fontSize="sm">
              There are no sales to display for this {timeframe}.
            </Text>
          </VStack>
        </VStack>
      ) : (
        <>
          <HStack justify={'center'}>
            <Box position="relative" width={300} height={300}>
              <PieChart width={300} height={300}>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={120}
                  outerRadius={135}
                  dataKey="value"
                  paddingAngle={5} // separates the pies
                  cornerRadius={50} // change border radius to semi circle at end
                  startAngle={45} // Adjust start angle to rotate the pie chart
                  endAngle={450} // Adjust end angle to rotate the pie chart
                >
                  {chartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
              <VStack
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                textAlign={'center'}
                gap={0}
              >
                {/* <Text fontSize="2xl" fontWeight="bold">
                  {chartData.reduce((acc, item) => acc + item.value, 0) + "%"}
                </Text> */}
                {/* <Text color={"text.secondary"}>Higher Sell</Text> */}
              </VStack>
            </Box>
          </HStack>

          <VStack gap={2} align={'space-between'} mt={4} fontSize={'lg'}>
            {chartData.map((item, index) => (
              <Box
                key={item.name}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <HStack>
                  <Box bg={COLORS[index]} h={4} w={4} rounded={'sm'} />
                  <Text color={'text.secondary'}>{item.name}</Text>
                </HStack>
                <Text color={'#000'} fontWeight={'semibold'}>
                  {getCurrencySymbol(totalSellData.currency)}
                  {item.value}
                </Text>
              </Box>
            ))}
          </VStack>
        </>
      )}
    </Card>
  );
};

export default TotalSell;
